import React, { createRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, Checkbox, Select, MenuItem, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import CountriesValidator from "../validators/CountriesValidator";
import PermitCompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";

import {
  useCountries,
  useAddCountry,
  useUpdateCountry,
  useDeleteCountry,
} from "../../../services/settings";
import {MaskedText, MaskedNumber, ComboSelect} from "../../../controls";
import __ from "lodash";
import { defaultComponents } from "../../../controls/Material-Table-Components";

const landingPermitRouteTypes = [
  { code: "NONE", value: "None" },
  { code: "BASIC", value: "Basic" },
  { code: "CARGO", value: "Traffic" },
  { code: "CARGO INBOUND", value: "Inbound Traffic" },
  { code: "CARGO OUTBOUND", value: "Outbound Traffic" },
];
const overflightPermitRouteTypes = [
  { code: "NONE", value: "None" },
  { code: "BASIC", value: "Basic" },
  { code: "CARGO", value: "Traffic" },
];

const CountryList = (_props) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth0();
  const validatorColumn = ValidatorColumn(CountriesValidator, true);

  const props = {
    ..._props,
  };
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null,
  });

  const tableRef = createRef({ state: { lastEditingRow: false } });

  const handleAddCountry = useAddCountry();
  const handleUpdateCountry = useUpdateCountry();
  const handleDeleteCountry = useDeleteCountry();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const countries = useCountries({ tableRef, queryClient, extraKeys });

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize: 14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars = {
      U: "[A-Z]",
      A: "[A-Z0-9]",
      a: "[A-Za-z0-9]",
      i: "[A-Za-z0-9\\-]",
      9: "[0-9]",
      D: "[0-9.]",
      "-": "-",
      x: ".",
    };
    const defaultEditProps = {
      style: { fontSize: "0.875rem" },
    };

    return [
      { ...validatorColumn },
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Country Code",
        field: "countryCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(2)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(50),
        title: "",
        field: "countryCode",
        initialEditValue: "",
        render: (rowData) => (<img style={{maxWidth:"50px", maxHeight:"30px"}} src={`https://flagsapi.com/${rowData.countryCode}/flat/64.png`} alt={`${rowData.countryCode} Flag`} /> )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Long Country Code",
        field: "countryCode3",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(3)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        title: "Country Name",
        field: "countryName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        title: "Country Search Name",
        field: "countrySearchName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(250,6,undefined,undefined,undefined,undefined,false),
        title: "Sanction Status",
        field: "sanctionStatus",
        editable: state.canEdit ? "always" : "never",
        render: (rowData) => (
            <ComboSelect
                group="SANCTION"
                subGroup="COUNTRY"
                fullWidth
                value={rowData.sanctionStatus}
                displayOnly={true}
                onChange={props.onChange}
            />
        ),
        editComponent: (props) => (
            <ComboSelect
                group="SANCTION"
                subGroup="COUNTRY"
                fullWidth
                value={props.value || ""}
                onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Blacklisted",
        field: "blacklisted",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: (rowData) => (
          <Checkbox
            style={{ padding: 0 }}
            checked={rowData.blacklisted}
            value="true"
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(250,6,undefined,undefined,undefined,undefined,false),
        title: "Routing Sanction",
        field: "routingSanctionStatus",
        editable: state.canEdit ? "always" : "never",
        render: (rowData) => (
            <ComboSelect
                group="SANCTION"
                subGroup="ROUTING"
                fullWidth
                value={rowData.routingSanctionStatus}
                displayOnly={true}
                onChange={props.onChange}
            />
        ),
        editComponent: (props) => (
            <ComboSelect
                group="SANCTION"
                subGroup="ROUTING"
                fullWidth
                value={props.value || ""}
                onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Overflight Restricted",
        field: "overflightRestricted",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: (rowData) => (
          <Checkbox
            style={{ padding: 0 }}
            checked={rowData.overflightRestricted}
            value="true"
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Landing Permit Supplier",
        field: "company.companyName",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <PermitCompanyUuidLookup
            companyType={"PERMIT_SUPPLIER"}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const supplier = returnObject.data;
                const data = {
                  ...props.rowData,
                  company: { companyName: supplier.companyName },
                  permitSupplierUuid: supplier.companyUuid,
                };
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
        render: (rowData) => (
          <div
            style={{
              width: 150,
              maxWidth: 150,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {rowData.company.companyName}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(20),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Overflight Permit Supplier",
        field: "overflightCompany.companyName",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <PermitCompanyUuidLookup
            companyType={"PERMIT_SUPPLIER"}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const supplier = returnObject.data;
                const data = {
                  ...props.rowData,
                  overflightCompany: { companyName: supplier.companyName },
                  overflightPermitSupplierUuid: supplier.companyUuid,
                };
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
        render: (rowData) => (
          <div
            style={{
              width: 150,
              maxWidth: 150,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {rowData.overflightCompany.companyName}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Permit Request Lead Time Hours",
        field: "permitRequestLeadTimeHours",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.permitRequestLeadTimeHours}
            decimalScale={0}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Permit Pre-Dep. Validity Hours",
        field: "permitPreDepartureValidityHours",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.permitPreDepartureValidityHours}
            decimalScale={0}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Permit Validity Hours",
        field: "permitValidityHours",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.permitValidityHours}
            decimalScale={0}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(20),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Landing Permit Route",
        field: "landingPermitRouteType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <Select
              value={props.value == null ? "" : props.value}
              onChange={(evt, data) => {
                if (data.props && data.props.children) {
                  const codeIndex = __.findIndex(landingPermitRouteTypes, {
                    value: data.props.children,
                  });
                  props.onChange(landingPermitRouteTypes[codeIndex].code);
                }
              }}
              renderValue={(value) => {
                const valueIndex = __.findIndex(landingPermitRouteTypes, {
                  code: value,
                });
                return `${
                  valueIndex < 0
                    ? ""
                    : landingPermitRouteTypes[valueIndex].value
                }`;
              }}
            >
              {landingPermitRouteTypes
                ? landingPermitRouteTypes.map((record) => {
                    return (
                      <MenuItem key={record.code} value={record.value}>
                        {record.value}
                      </MenuItem>
                    );
                  })
                : null}
            </Select>
          );
        },
        render: (rowData) => {
          const valueIndex = __.findIndex(landingPermitRouteTypes, {
            code: rowData.landingPermitRouteType,
          });
          return (
            <div>
              {valueIndex < 0 ? "" : landingPermitRouteTypes[valueIndex].value}
            </div>
          );
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Overflight Permit Route",
        field: "overflightPermitRouteType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <Select
              value={props.value == null ? "" : props.value}
              onChange={(evt, data) => {
                if (data.props && data.props.children) {
                  const codeIndex = __.findIndex(overflightPermitRouteTypes, {
                    value: data.props.children,
                  });
                  props.onChange(overflightPermitRouteTypes[codeIndex].code);
                }
              }}
              renderValue={(value) => {
                const valueIndex = __.findIndex(overflightPermitRouteTypes, {
                  code: value,
                });
                return `${
                  valueIndex < 0
                    ? ""
                    : overflightPermitRouteTypes[valueIndex].value
                }`;
              }}
            >
              {overflightPermitRouteTypes
                ? overflightPermitRouteTypes.map((record) => {
                    return (
                      <MenuItem key={record.code} value={record.value}>
                        {record.value}
                      </MenuItem>
                    );
                  })
                : null}
            </Select>
          );
        },
        render: (rowData) => {
          const valueIndex = __.findIndex(overflightPermitRouteTypes, {
            code: rowData.overflightPermitRouteType,
          });
          return (
            <div>
              {valueIndex < 0
                ? ""
                : overflightPermitRouteTypes[valueIndex].value}
            </div>
          );
        },
      },
    ];
  };

  return (
    isAuthenticated && (
      <div style={{ width: "100%", marginLeft: 20, marginRight: 20 }}>
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            pageSize: 10,
            detailPanelType: "single",
            // paging: false,
            // overflowY: "scroll",
            // minBodyHeight: "calc(100vh - 230px)", //168+60//FIXME to calculate dynamic height, needed for correct scroll position identification
            // maxBodyHeight: "calc(100vh - 230px)"
          }}
          components={{
            ...defaultComponents,
            Pagination: (props) => (
              <TablePagination {...props} rowsPerPageOptions={[5, 10, 15]} />
            ),
          }}
          title="Country Management"
          columns={getColumns({ canEdit: state.canEdit })}
          data={countries.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
              ? (newData) =>
                  new Promise((resolve, reject) => {
                    handleAddCountry.mutate({
                      record: newData,
                      resolve,
                      reject,
                      extraKeys,
                    });
                  })
              : undefined,
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateCountry.mutate({
                  record: newData,
                  oldRecord: oldData,
                  resolve,
                  reject,
                  extraKeys,
                });
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteCountry.mutate({
                  oldRecord: oldData,
                  resolve,
                  reject,
                  extraKeys,
                });
              }),
          }}
        />
      </div>
    )
  );
};

export default CountryList;
